import React, { useEffect, useState, useCallback, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@mui/styles';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import Rubberband from './rubberband';

import { chunk as _chunk, debounce as _debounce, get as _get } from 'lodash';
import sizeMe from 'react-sizeme';
import { alpha } from '@mui/material/styles';

const charBtnSize = 25;

const useStyles = makeStyles(theme => ({
    root: {
        margin: 10,
        marginBottom: 35,
    },
    row: {
        padding: 0,
        userSelect: 'none',
    },
    heading: {
        paddingTop: 0,
        opacity: 0.4,
    },
    column: {
        paddingLeft: 10,
        paddingRight: 18,
        whiteSpace: 'nowrap',
        '& div': {
            display: 'inline',
        },
        userSelect: 'none',
        maxHeight: charBtnSize,
    },
    hexchar: {
        maxWidth: charBtnSize,
        maxHeight: charBtnSize,
        minWidth: charBtnSize,
        minHeight: charBtnSize,
        height: charBtnSize,
        width: charBtnSize,
        display: 'inline-block',
        userSelect: 'none',
        paddingLeft: 3,
        paddingRight: 3,

        '&:hover': {
            background: `${alpha(theme.palette.text.primary, 0.08)}`,
        },
    },
    text: {
        paddingTop: 5,
        paddingBottom: 5,
        paddingLeft: 0,
        paddingRight: 0,
        fontFamily: 'Monospace',
        display: 'block',
        wordBreak: 'break-all',
        fontSize: 16,
        lineHeight: 1.9,

        position: 'relative',
    },
    selection: {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.secondary.contrastText,
        width: 9.61,
        position: 'absolute',
        margin: 0,
    },
    char: {
        width: 9.61,
        position: 'absolute',
        margin: 0,
    },
    hexSelected: {
        color: theme.palette.secondary.main,
    },
}));

const charsPerColumn = 8;

const HexDumpRow = ({ data = [], ...props }) => {
    const classes = useStyles();

    // asciiBtnSize / 2 + padding
    const getColumnWidth = useCallback(() => charsPerColumn * (charBtnSize / 2 + 1) + 30, []);

    return (
        <React.Fragment>
            {data.map((column, i) =>
                <Box
                    className={classes.column}
                    style={{ minWidth: getColumnWidth() }}
                    key={`${i}`}>
                    <span>
                        {column.map((char, j) =>
                            char && <HexChar
                                onClick={props.onSelection}
                                char={char}
                                key={`${props.row}_${i}_${j}`}
                                dakey={`${props.row}_${i}_${j}`}
                                selected={props.selection[`${props.row}_${i}_${j}`]}/>
                        )}
                    </span>
                </Box>
            )}
        </React.Fragment>
    );
};

HexDumpRow.propTypes = {
    data: PropTypes.array,
    onSelection: PropTypes.func,
    selection: PropTypes.object,
    row: PropTypes.number
};

const HexChar = memo(({ dakey, selected, char, onClick }) => {
    const classes = useStyles();

    // padded ascii value
    const ascii = useCallback(c => c.charCodeAt(0).toString(16).padStart(2, '0'), []);

    return (
        <Typography
            component="span"
            fontFamily="Monospace"
            data-dakey={dakey}
            onClick={() => onClick([dakey])}
            className={classes.hexchar}
            aria-label={char}
            size="small"
            style={{ cursor: 'pointer' }}
            color={selected ? 'secondary' : 'primary' }>
            {ascii(char)}
        </Typography>
    );
});
HexChar.displayName = 'HexChar';
HexChar.propTypes = {
    onClick: PropTypes.func,
    selected: PropTypes.bool,
    dakey: PropTypes.string,
    char: PropTypes.string,
};

const TextChar = memo(({ dakey, selected, top, left, char }) => {
    const theme = useTheme();
    const classes = useStyles();

    const text = useCallback(char => char ? char.replace(/[^\x20-\x7E]/g, '·') : '', []);

    return (
        <span
            data-dakey={dakey}
            className={selected ? classes.selection : classes.char}
            style={{
                color: text(char) === '·'
                    ? theme.palette.grey[600]
                    : '',
                top,
                //for Monospace font 16
                left,
                fontFamily: 'Monospace',
            }}>
            {text(char)}
        </span>
    );
});
TextChar.displayName = 'TextChar';
TextChar.propTypes = {
    selected: PropTypes.bool,
    dakey: PropTypes.string,
    top: PropTypes.number,
    left: PropTypes.number,
    char: PropTypes.string,
};

const Hexdump = sizeMe()(({
    data = '',
    ...props
}) => {
    const classes = useStyles();
    const rubberbandParentRef = useRef(null);
    const [selection, setSelection] = useState({});
    const [rows, setRows] = useState([]);
    const [numColumns, setNumColumns] = useState(0);

    const getHeading = useCallback(i => ('00000000' + i.toString(16)).substr(-8) + ':', []);

    // incoming
    const handleSelectionChanged = useCallback(s => {
        setSelection(current => {
            const sel = s.reduce((a, c) => ({ ...a, [c]: true }), {});
            if (current.selector === 'text') {
                return { selector: 'hex', ...sel };
            }
            return { selector: 'hex', ...sel };
        });
    }, []);

    // outgoing
    const handleTextSelection = () => {
        let t = '';
        if (window.getSelection) {
            t = window.getSelection().toString();
        } else if (document.selection && document.selection.type !== 'Control') {
            t = document.selection.createRange().text;
        }

        if (!t || !t.length) {
            setSelection({});
            return;
        }

        const r = window.getSelection().getRangeAt(0);
        const startKey = _get(r, 'startContainer.dataset.dakey') ||
            _get(r, 'startContainer.parentNode.dataset.dakey');
        const endKey = _get(r, 'endContainer.dataset.dakey') ||
            _get(r, 'endContainer.parentNode.dataset.dakey');
        if (!startKey || !endKey) return;

        const s = startKey.split('_').map(s => +s);
        const e = endKey.split('_').map(e => +e);
        if (s.length < 3 || e.length < 3) return;

        setSelection(current => {
            const sel = { selector: 'text' };
            let rs = s[0];
            let startOffset = r.startOffset > 0;
            for (; rs <= e[0]; rs++) {
                let cs = rs === s[0] ? s[1] : 0;
                const ce = rs === e[0] ? e[1] : numColumns - 1;
                for (; cs <= ce; cs++) {
                    let chs = rs === s[0] && cs === s[1] ? s[2] : 0;
                    const che = rs === e[0] && cs === e[1] ? e[2] : charsPerColumn - 1;
                    for (; chs <= che; chs++) {
                        if (startOffset) {
                            startOffset = false;
                            continue;
                        }
                        if (rs === e[0] && cs === ce && chs === che && !r.endOffset) {
                            continue;
                        }
                        sel[`${rs}_${cs}_${chs}`] = true;
                    }
                }
            }
            return sel;
        });

        // clear text selection
    };

    let dismissMouseUp = 0;
    let doucleckicked = false;
    const onMouseUp = () => {
        _debounce(() => {
            if (doucleckicked) {
                doucleckicked = false;
                dismissMouseUp++;
            } else if (dismissMouseUp > 0) {
                dismissMouseUp--;
            } else {
                handleTextSelection();
            }
        }, 200)();
    };

    const onDoubleClick = e => {
        e.stopPropagation();
        doucleckicked = true;
        handleTextSelection();
    };

    // main hook
    useEffect(() => {
        let dc;
        try {
            dc = atob(data);
        } catch (err) {
            // string is not base-64 encoded
            dc = data;
        }

        const calculateColumns = () => {
            const w = Math.max(props.size.width || 0, 300);
            const space = 3 * w / 4;
            const numColumns = space / (charsPerColumn * charBtnSize + 35);
            return Math.floor(numColumns);
        };

        // assist from https://stackoverflow.com/a/38851957/5895550
        const padarray = (arr, len, fill) => arr.concat(Array(len).fill(fill)).slice(0, len);

        const clms = calculateColumns();
        // split into rows and columns (_chunk ftw)
        const rws = _chunk(
            _chunk([...dc], charsPerColumn),
            clms
        );

        if (rws.length > 0) {
            const lastrow = rws[rws.length - 1];
            if (lastrow.length > 0) {
                const lastcol = lastrow[lastrow.length - 1];
                lastrow[lastrow.length - 1] = padarray(lastcol, charsPerColumn, null);
            }
            rws[rws.length - 1] = padarray(lastrow, clms, padarray([], charsPerColumn, null));
        }
        setSelection({});
        setRows(rws);
        setNumColumns(clms);
    }, [data, props.size]);

    // ref={mainRef}
    return (
        <div className={classes.root}>
            <Grid container spacing={3}>
                <Grid item xs={9} ref={rubberbandParentRef}>
                    <List style={{ userSelect: 'none' }}>
                        <Rubberband
                            onSelection={handleSelectionChanged}
                            selector="span"
                            dakey="dakey"
                            anchorEl={rubberbandParentRef.current}
                            slack={5}
                            size={props.size}/>
                        {rows.map((r, i) =>
                            <ListItem key={'' + i} className={classes.row}>
                                <Typography variant="button" className={classes.heading}>{getHeading(i)}</Typography>
                                <HexDumpRow
                                    selection={selection}
                                    row={i}
                                    data={r}
                                    onSelection={handleSelectionChanged}
                                />
                            </ListItem>
                        )}
                    </List>
                </Grid>
                <Grid item xs={3}>
                    <div
                        className={classes.text}
                        onMouseUp={onMouseUp}
                        onDoubleClick={onDoubleClick}>
                        {rows.map((r, i) =>
                            r.map((c, j) =>
                                c.map((char, k) => (
                                    <TextChar
                                        char={char}
                                        key={`${i}_${j}_${k}`}
                                        dakey={`${i}_${j}_${k}`}
                                        selected={selection[`${i}_${j}_${k}`]}
                                        top={i * charBtnSize + 7}
                                        left={(j * charsPerColumn + k) * 9.61} />
                                ))
                            )
                        )}
                    </div>
                </Grid>
            </Grid>
        </div>
    );
});

Hexdump.propTypes = {
    data: PropTypes.string,
    // charsPerColumn: PropTypes.number,

    // provided by sizeMe
    size: PropTypes.object,
};

export {
    Hexdump as default,
};
